import { useEffect, useRef, useState } from 'react';

import { useModal, useModels, useRequest } from '../../hooks';
import { notify } from '../../app';
import { createModel, deleteModel, updateModel } from '../../api';
import { ModelType } from './const';
import type {
  AiModelPayload,
  AiModelData,
  NodeJSON,
  AiModelResponse,
} from '../../api';

export type ModelsManagementFormProps = {
  modelType: ModelType | string;
  modelId: string;
  settings?: string;
  settingsValidationSchema?: string;
  uiSchema?: string;
};

const defaultForm: ModelsManagementFormProps = {
  modelType: '',
  modelId: '',
  settings: JSON.stringify({} as NodeJSON),
  settingsValidationSchema: JSON.stringify({} as NodeJSON),
  uiSchema: JSON.stringify({} as NodeJSON),
};

export const useModelsManagement = () => {
  const { isOpened, onClose, onOpen } = useModal();

  const {
    isOpened: openedDeleteModal,
    onOpen: openDeleteModal,
    onClose: closeDeleteModal,
  } = useModal();

  const {
    llmModels = [],
    ttsModels = [],
    sttModels = [],
    loadingModels,
  } = useModels();

  const [formId, setFormId] = useState(1);
  const [form, setForm] = useState<ModelsManagementFormProps>(defaultForm);
  const [currentModels, setCurrentModels] = useState<AiModelData[]>([]);
  const [currentModel, setCurrentModel] = useState<AiModelData | null>(null);
  const [allModels, setAllModels] = useState<
    Record<ModelType, AiModelData[] | null>
  >({
    [ModelType.LLM]: llmModels,
    [ModelType.TTS]: ttsModels,
    [ModelType.STT]: sttModels,
  });
  const deleteModelRef = useRef<{ id: string; type: ModelType } | null>(null);

  const prevModelType = useRef<string | null>(null);

  useEffect(() => {
    if (form?.modelType && form.modelType !== prevModelType.current) {
      setForm({
        modelType: form.modelType,
        modelId: '',
        settings: JSON.stringify({}),
        settingsValidationSchema: JSON.stringify({}),
        uiSchema: JSON.stringify({}),
      });
      setFormId(f => f + 1);
    }
    prevModelType.current = form?.modelType;
  }, [form?.modelType]);

  const [handleCreateModel, isModelCreating] = useRequest(
    (
      _signal,
      {
        modelName,
        providerName,
        settings,
        settingsValidationSchema,
        uiSchema,
        type,
      }: AiModelPayload
    ) =>
      createModel({
        modelName,
        providerName,
        settings,
        settingsValidationSchema,
        uiSchema,
        type,
      }),
    {
      onSuccess: (data: AiModelResponse) => {
        const modelType = data?.type as unknown as ModelType;
        setAllModels(prev => {
          const safePrev: Record<ModelType, AiModelData[]> = {
            [ModelType.LLM]: prev?.[ModelType.LLM] ?? [],
            [ModelType.TTS]: prev?.[ModelType.TTS] ?? [],
            [ModelType.STT]: prev?.[ModelType.STT] ?? [],
          };

          const label = `${data.providerName}:${data.modelName}`;
          return {
            ...safePrev,
            [modelType]: [
              ...safePrev[modelType],
              {
                ...data,
                value: data.id,
                label,
              },
            ],
          };
        });

        onClose?.();
        notify.success('Model created successfully');
      },
      onError: () => {
        notify.error('Failed to create new model');
      },
      deps: [],
    }
  );

  const [handleDeleteModel, isModelDeleting] = useRequest(
    (_signal, id: string, type: ModelType) => {
      deleteModelRef.current = { id, type };
      return deleteModel(id);
    },
    {
      onSuccess: () => {
        const { id, type } = deleteModelRef.current!;

        setAllModels(prev => {
          const safePrev: Record<ModelType, AiModelData[]> = {
            [ModelType.LLM]: prev?.[ModelType.LLM] ?? [],
            [ModelType.TTS]: prev?.[ModelType.TTS] ?? [],
            [ModelType.STT]: prev?.[ModelType.STT] ?? [],
          };

          const updated = safePrev[type].filter(model => model.id !== id);

          return {
            ...safePrev,
            [type]: updated,
          };
        });

        closeDeleteModal?.();
        setCurrentModel(null);
        setCurrentModels([]);
        setForm({ ...defaultForm, modelType: type });
        setFormId(f => f + 1);
        notify.success('Model deleted successfully');
      },
      onError: () => {
        notify.error('Failed to delete model');
      },
      deps: [],
    }
  );

  const [handleUpdateModel, isModelUpdating] = useRequest(
    _signal => {
      const { settings, settingsValidationSchema, uiSchema, modelType } = form;
      const {
        modelName = '',
        providerName = '',
        id: modelId = '',
      } = currentModel as AiModelData;

      const payload = {
        modelName,
        providerName,
        settings: JSON.parse(settings as string),
        settingsValidationSchema: JSON.parse(
          settingsValidationSchema as string
        ),
        uiSchema: JSON.parse(uiSchema as string),
        type: modelType as any,
      };

      return updateModel(modelId, payload);
    },
    {
      onSuccess: () => {
        notify.success('Model updated successfully');
      },
      onError: () => {
        notify.error('Failed to update model');
      },
      deps: [
        form.modelId,
        form.modelType,
        form.settings,
        form.uiSchema,
        form.settingsValidationSchema,
      ],
    }
  );

  useEffect(() => {
    if (!form?.modelType) return;

    const modelType = form.modelType as ModelType;
    const selectedModels = allModels?.[modelType] ?? [];

    const mappedModels = selectedModels.map((d: AiModelData) => ({
      ...d,
      value: d.id,
      label: d.label,
    }));

    setCurrentModels(mappedModels as AiModelData[]);
  }, [form?.modelType, allModels]);

  useEffect(() => {
    if (form?.modelId) {
      const matched = currentModels.find(m => m.value === form.modelId);

      setCurrentModel(matched || null);
      if (matched && !openedDeleteModal) {
        setForm(prev => ({
          ...prev,
          settings: JSON.stringify(matched.settings || {}),
          uiSchema: JSON.stringify(matched.uiSchema || {}),
          settingsValidationSchema: JSON.stringify(
            matched.settingsValidationSchema || {}
          ),
        }));
        setFormId(f => f + 1);
      }
    }
  }, [form?.modelId, currentModels, openedDeleteModal]);

  useEffect(() => {
    setAllModels({
      [ModelType.TTS]: ttsModels,
      [ModelType.LLM]: llmModels,
      [ModelType.STT]: sttModels,
    });
  }, [llmModels, sttModels, ttsModels]);

  return {
    form,
    formId: String(formId),
    onChange: setForm,
    setFormId,
    setForm,
    loadingModels,
    currentModels,
    isOpened,
    onOpen,
    onClose,
    isModelCreating,
    handleCreateModel,
    handleUpdateModel,
    isModelUpdating,
    setCurrentModels,
    handleDeleteModel,
    isModelDeleting,
    openedDeleteModal,
    openDeleteModal,
    closeDeleteModal,
    allModels,
  };
};
